// components/SourceThumb.tsx — CV3: the small image thumb a landed capture
// shows on the job screen. Reuses fetchSourceOriginal's object-URL pattern
// (lib/api.ts — the tenant header cannot ride an <img src>, so the bytes are
// fetched and served to the tag as an object URL, revoked on unmount).
//
// Deliberately narrow: it renders ONLY when the filename says image AND the
// served mime agrees (the server's stored type is the truth — a mislabeled
// file renders nothing rather than a broken tag), and it fetches nothing for
// non-images, so a job full of PDFs costs zero extra requests. What it does
// cost is one fetch per image the job actually shows — bounded by the job's
// own captures, never the shop (requestBudget.test.tsx's stub tenant has no
// sources, and grow() adds none, so the no-fan-out guard holds its line).
//
// CV5 §5: it also renders an AUDIO MARK. A voice note's filename says `.webm`
// and nothing here matched it, so a landed recording drew an empty frame with
// a bare label beside it — the row gave no sign that there was anything to
// hear. The mark costs NO request (it is drawn, not fetched), so the
// no-fan-out guard's line is exactly where it was.
import { useEffect, useState } from 'preact/hooks';
import { fetchSourceOriginal } from '../lib/api';
import { looksLikeAudio } from '../lib/audioSupport';
import './SourceThumb.css';

const IMAGE_EXT = /\.(png|jpe?g|gif|webp)$/i;

/** Whether a stored filename looks like an image — the pre-fetch gate. */
export function looksLikeImage(filename: string | null | undefined): boolean {
  return typeof filename === 'string' && IMAGE_EXT.test(filename);
}

export interface SourceThumbProps {
  tenantId: string;
  sourceId: string;
  filename: string | null | undefined;
}

export function SourceThumb({ tenantId, sourceId, filename }: SourceThumbProps) {
  const [url, setUrl] = useState<string | null>(null);
  // CV5 §5 — a recording announces itself without a byte being fetched.
  const isAudio = looksLikeAudio(filename);

  useEffect(() => {
    setUrl(null);
    if (!looksLikeImage(filename)) return;
    let cancelled = false;
    let revoke: string | null = null;
    fetchSourceOriginal(tenantId, sourceId)
      .then((orig) => {
        if (cancelled || !orig.mimeType.startsWith('image/')) {
          URL.revokeObjectURL(orig.objectUrl);
          return;
        }
        revoke = orig.objectUrl;
        setUrl(orig.objectUrl);
      })
      .catch(() => {
        // A thumb that cannot load renders nothing — the row's own
        // filename/kind line is already the honest fact.
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tenantId, sourceId, filename]);

  if (isAudio) {
    return (
      <span class="source-thumb source-thumb--audio" data-testid="source-thumb-audio" aria-hidden="true">
        ▶
      </span>
    );
  }
  if (!url) return null;
  return <img class="source-thumb" src={url} alt="" />;
}
